import {
  Eventcalendar,
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventUpdateEvent,
  MbscPageChangeEvent,
  MbscResource
} from '@mobiscroll/react'
import { Card, Grid } from '@mui/material'
import dayjs from 'dayjs'
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import {
  AuthContext,
  DUMMY_NEW_APPOINTMENT,
  NewAppointmentContext,
  NotificationContext
} from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useUpdateAppointmentMutation } from '../../redux/api'
import { defaultTimeZoneDateToUtcDate } from '../../utils'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
import { ProposeNewDayTimeDialog } from '../AppointmentPanel/ProposeNewDayTimeDialog'
import { useRenderCalendar } from '.'
import { renderEventBlock, renderEventBlockForNew } from './useRenderEventBlock'

export const AppointmentCalendar: FC = () => {
  const { currentUser } = useContext(AuthContext)
  const { setNotice } = useContext(NotificationContext)
  const accessToken = loggingAssert(
    'No access token found where one was expected',
    currentUser?.accessToken
  )
  const { shopProfile } = useContext(AppContext)

  const { renderDayHeader, renderHeader, renderResource } = useRenderCalendar()

  const [updateAppointment] = useUpdateAppointmentMutation()
  const {
    date,
    setDate,
    selectedResource,
    setSelectedResource,
    setOpenPanel,
    openPanel,
    appointments,
    shopResources,
    customersMap,
    vehiclesMap,
    apptToRepairOrdersMap,
    statuses,
    roToTechniciansMap,
    selectedAppointment,
    setSelectedAppointment,
    selectedCustomer,
    selectedVehicle,
    selectedColor,
    setStartDate,
    setEndDate
  } = useContext(NewAppointmentContext)

  const [view, setView] = useState<'week' | 'day'>('week')
  const [calendar, setCalendar] = useState<'appt' | 'resource'>('appt')
  const changeView = (event: React.SyntheticEvent, newView: 'week' | 'day') => {
    setView(newView)
  }
  const changeCal = (
    event: React.SyntheticEvent,
    newCal: 'appt' | 'resource'
  ) => {
    if (newCal !== null) {
      setCalendar(newCal)
    }
  }
  const existingAppointments = useMemo(() => {
    if (!shopResources || !appointments) {
      return []
    }
    return appointments.map((appointment) => {
      const { info } = appointment
      const { startDateTime, concern, estimatedWorkHours, resources } = info!
      const endDateTime = dayjs(localDateTimeArrayToDate(startDateTime))
        .add(estimatedWorkHours || 1, 'hour')
        .toDate()
      const filterdResources = shopResources
        .filter((resource) => (resources || []).includes(resource.id!))
        .map((resource) => resource.id!)
      if (filterdResources.length === 0) {
        filterdResources.push('Unassigned')
      }
      return {
        id: appointment.id,
        start: localDateTimeArrayToDate(startDateTime),
        end: endDateTime,
        title: concern,
        resource: filterdResources
      }
    })
  }, [appointments, shopResources])

  const newAppointment = useMemo(() => {
    return {
      id: 'new',
      start: date,
      end: dayjs(date).add(1, 'hour').toDate(),
      title: 'New Appointment',
      resource: selectedResource,
      color: 'yellow'
    }
  }, [date, selectedResource])

  const [data, setData] = useState<
    {
      id: string | undefined
      start: Date
      end: Date
      title: string | undefined
      resource: string[]
    }[]
  >([])

  useEffect(() => {
    if (selectedAppointment?.id === 'new') {
      setData(existingAppointments.concat(newAppointment))
    } else {
      setData(existingAppointments)
    }
  }, [existingAppointments, newAppointment, selectedAppointment])

  const resourceData = useMemo<MbscResource[]>(() => {
    if (!shopResources) {
      return [{ id: 'Unassigned', name: 'Unassigned' }]
    }
    return [{ id: 'Unassigned', name: 'Unassigned' }].concat(
      shopResources.map((resource) => {
        const { info } = resource
        const { name } = info!
        return {
          id: resource.id || '',
          name: name || ''
        }
      })
    )
  }, [shopResources])

  const [proposeNewDateTimeInfo, setProposeNewDateTimeInfo] = useState<
    | {
        appointment: SerializableAppointmentEntry
        oldTiming: Date
        newTiming: Date
      }
    | undefined
  >(undefined)

  const handleEventUpdate = async (args: MbscEventUpdateEvent) => {
    const calendarEvent = args.event
    const { id, start, resource } = calendarEvent
    if (!id) {
      return false
    }
    if (id === 'new') {
      setDate(start as Date)
      setSelectedResource(resource as string[])
      return true
    }
    const apptToModify = appointments?.find((appt) => appt.id === id)
    if (!apptToModify || !apptToModify.info) {
      return false
    }

    if (
      !apptToModify.info.isConfirmed &&
      args.oldEvent.start !== args.event.start
    ) {
      setProposeNewDateTimeInfo({
        appointment: apptToModify,
        oldTiming: args.oldEvent.start as Date,
        newTiming: args.event.start as Date
      })

      return false
    }

    await updateAppointment({
      authorization: accessToken,
      appointmentEntry: {
        id: apptToModify.id,
        info: {
          ...apptToModify.info,
          startDateTime: defaultTimeZoneDateToUtcDate(start as Date),
          resources: ((resource as string[]) || []).filter(
            (uuid) => uuid !== 'Unassigned'
          )
        }
      }
    })
    setNotice('The appointment has been updated.')
    return true
  }

  const updateColor = useCallback(
    (color: string, exAppt: SerializableAppointmentEntry) => {
      updateAppointment({
        authorization: accessToken,
        appointmentEntry: {
          id: exAppt.id,
          info: {
            ...exAppt.info,
            startDateTime: defaultTimeZoneDateToUtcDate(
              localDateTimeArrayToDate(exAppt.info!.startDateTime)
            ),
            color
          }
        }
      })
    },
    [accessToken, updateAppointment]
  )

  const handleEventClick = (args: MbscEventClickEvent) => {
    const calendarEvent = args.event
    const { id } = calendarEvent
    setSelectedAppointment(appointments?.find((a) => a.id === id))
    setOpenPanel(true)
  }

  const handleCellClick = (args: MbscCellClickEvent) => {
    const cellStartDate = args.date
    let cellResource: string[] = []
    if (calendar === 'resource') {
      cellResource = [args.resource.toString()]
    }
    setDate(cellStartDate)
    setSelectedResource(cellResource)
    setSelectedAppointment(DUMMY_NEW_APPOINTMENT)
    setOpenPanel(true)
  }
  const onPageChange = (event: MbscPageChangeEvent) => {
    const { firstDay, lastDay } = event
    setStartDate(firstDay)
    setEndDate(lastDay)
  }

  return (
    <>
      {proposeNewDateTimeInfo && (
        <ProposeNewDayTimeDialog
          open={!!proposeNewDateTimeInfo}
          handleCancel={() => {
            // Revert back to original start datetime
            const updatedData = [...data]
            const indexToUpdate = updatedData.findIndex(
              (appt) => appt.id === proposeNewDateTimeInfo.appointment.id
            )

            if (indexToUpdate !== -1) {
              updatedData[indexToUpdate].start =
                proposeNewDateTimeInfo.oldTiming
              updatedData[indexToUpdate].end = dayjs(
                proposeNewDateTimeInfo.oldTiming
              )
                .add(
                  proposeNewDateTimeInfo.appointment.info.estimatedWorkHours ||
                    1,
                  'hour'
                )
                .toDate()
            }

            setData(updatedData)

            setProposeNewDateTimeInfo(undefined)
          }}
          handleClose={() => {
            setProposeNewDateTimeInfo(undefined)
          }}
          appointment={proposeNewDateTimeInfo.appointment}
          customer={
            customersMap[proposeNewDateTimeInfo.appointment.info!.customer!]!
          }
          vehicle={
            vehiclesMap[proposeNewDateTimeInfo.appointment.info!.vehicle!]!
          }
          defaultNewDateTime={dayjs(proposeNewDateTimeInfo.newTiming)}
        />
      )}
      <Grid item xs={openPanel ? 8 : 12}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: '4px',
            height: '90vh'
          }}>
          <Eventcalendar
            view={{
              schedule: {
                type: view,
                startDay: shopProfile?.info!.calendarStartDay || 0,
                endDay: shopProfile?.info!.calendarEndDay || 5,
                startTime:
                  shopProfile
                    ?.info!.calendarStartTime!.toTimeString()
                    .slice(0, 5) || '05:00',
                endTime:
                  shopProfile
                    ?.info!.calendarEndTime!.toTimeString()
                    .slice(0, 5) || '20:00'
              }
            }}
            refDate={dayjs(new Date()).subtract(1, 'day').format('YYYY-MM-DD')}
            theme="material"
            themeVariant="light"
            onCellClick={handleCellClick}
            data={data}
            dragToMove
            groupBy="date"
            onEventUpdate={handleEventUpdate}
            onEventClick={handleEventClick}
            renderDay={renderDayHeader.bind(null, view)}
            renderHeader={renderHeader.bind(null, {
              calendar,
              view,
              changeCal,
              changeView
            })}
            renderScheduleEvent={(eventData) => {
              const appt = appointments?.find((a) => a.id === eventData.id)
              return appt
                ? renderEventBlock(
                    appt,
                    customersMap[appt.info!.customer!]!,
                    vehiclesMap && appt.info!.vehicle
                      ? vehiclesMap[appt.info!.vehicle]
                      : undefined,
                    apptToRepairOrdersMap[appt.id!],
                    statuses,
                    roToTechniciansMap,
                    calendar,
                    view,
                    selectedAppointment?.id! === eventData.id!,
                    updateColor
                  )
                : renderEventBlockForNew(
                    calendar,
                    view,
                    selectedColor,
                    selectedCustomer,
                    selectedVehicle
                  )
            }}
            resources={calendar === 'resource' ? resourceData : null}
            renderResource={
              calendar === 'resource' ? renderResource : undefined
            }
            data-testid="calendar"
            showEventTooltip={false}
            onPageChange={onPageChange}
          />
        </Card>
      </Grid>
    </>
  )
}
